import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiGet } from "../../lib/http";
import { duration, tracePath, type TraceList } from "../../lib/neio";
import { neioState } from "../neio/TraceView";
import { Empty } from "../States";
import { TableScroll } from "../TableScroll";
import { utcTime } from "./format";

export const TRACE_PAGE = 50;

/** The explorer's rows, without the fetching. */
export function TraceTable({ data, offset, onPage }: { data: TraceList; offset: number; onPage: (offset: number) => void }) {
  if (data.traces.length === 0) return <Empty title="No traces match">Clear the search, or search by run id, job id or tag.</Empty>;
  const last = offset + data.traces.length;
  return (
    <div className="stack" style={{ gap: 8 }}>
      <TableScroll label="NeIO Core traces" className="card" style={{ padding: 0 }}>
        <table>
          <thead><tr><th>When</th><th>Question</th><th>Steps</th><th>Time</th><th>Tokens</th></tr></thead>
          <tbody>
            {data.traces.map((t) => (
              <tr key={t.request_id}>
                <td className="num nowrap">{utcTime(t.created_at)}</td>
                <td className="wrap-anywhere"><Link to={tracePath(t.request_id)}>{t.question || t.request_id}</Link></td>
                <td className="num">{t.node_count ?? ""}</td>
                <td className="num nowrap">{duration(t.duration_ms)}</td>
                <td className="num">{t.total_tokens?.toLocaleString("en-US") ?? ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScroll>
      <div className="row" style={{ fontSize: 13 }}>
        <span className="muted num">{offset + 1} to {last} of {data.total}</span>
        <button className="ghost" disabled={offset === 0} onClick={() => onPage(Math.max(0, offset - TRACE_PAGE))}>Previous</button>
        <button className="ghost" disabled={last >= data.total} onClick={() => onPage(offset + TRACE_PAGE)}>Next</button>
      </div>
    </div>
  );
}

/** /admin/traces?q=&offset=. Every package's traces: admins only, as on the server. */
export function TracesTab() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") ?? "";
  const offset = Math.max(0, Number(params.get("offset")) || 0);
  const [draft, setDraft] = useState(q);
  const query = useQuery({
    queryKey: ["neio", "explorer", q, offset],
    queryFn: () => apiGet("/api/v1/neio/traces", { query: { q: q || undefined, limit: TRACE_PAGE, offset } }),
    retry: false,
  });
  const go = (next: { q?: string; offset?: number }) => {
    const p = new URLSearchParams();
    const nq = next.q ?? q;
    const no = next.offset ?? 0;
    if (nq) p.set("q", nq);
    if (no) p.set("offset", String(no));
    setParams(p);
  };
  const state = neioState({
    what: "Reading the NeIO Core traces", loading: query.isPending, status: undefined, error: query.error,
    onRetry: () => void query.refetch(), notFound: <Empty title="NeIO Core has no traces yet" />,
  });
  return (
    <>
      <form className="filters" role="search" aria-label="Search traces" onSubmit={(e) => { e.preventDefault(); go({ q: draft.trim() }); }}>
        <label className="field">
          <span>Search</span>
          <input value={draft} placeholder="Run id, job id, tag or words" maxLength={200} onChange={(e) => setDraft(e.target.value)} />
        </label>
        <button type="submit"><Search size={14} aria-hidden="true" /> Search</button>
      </form>
      {state ?? (query.data && <TraceTable data={query.data} offset={offset} onPage={(o) => go({ offset: o })} />)}
    </>
  );
}
